import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthActions } from '@convex-dev/auth/react';
import { toast } from 'sonner';
import { CreditCardIcon } from '@phosphor-icons/react';
import { useLocalization } from '@/context/LocalizationContext';

const Signup = () => {
  const navigate = useNavigate();
  const { signIn } = useAuthActions();
  const { t } = useLocalization();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth.signup.passwordMismatch'));
      return;
    }

    setLoading(true);

    try {
      await signIn('password', {
        flow: 'signUp',
        email: formData.email,
        password: formData.password
      });
      toast.success(t('auth.signup.success'));
      navigate('/');
    } catch (error) {
      toast.error(error.message || t('auth.signup.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-zinc-900 px-4 transition-colors">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none p-8">
          <div className="flex items-center justify-center mb-8">
            <CreditCardIcon size={40} className="text-[#002FA7] dark:text-blue-400" weight="regular" />
          </div>
          <h1 className="font-outfit text-3xl tracking-tight text-zinc-900 dark:text-zinc-100 text-center mb-2">
            {t('auth.signup.title')}
          </h1>
          <p className="font-dmsans text-sm text-zinc-500 dark:text-zinc-400 text-center mb-8">
            {t('auth.signup.subtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-2 block">
                {t('auth.fields.email')}
              </label>
              <input
                data-testid="signup-email-input"
                type="email"
                required
                className="w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-none px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7]/20 dark:focus:ring-blue-400/20 focus:border-[#002FA7] dark:focus:border-blue-400 focus:outline-none font-dmsans text-zinc-900 dark:text-zinc-100"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <label className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-2 block">
                {t('auth.fields.password')}
              </label>
              <input
                data-testid="signup-password-input"
                type="password"
                required
                className="w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-none px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7]/20 dark:focus:ring-blue-400/20 focus:border-[#002FA7] dark:focus:border-blue-400 focus:outline-none font-dmsans text-zinc-900 dark:text-zinc-100"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            <div>
              <label className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-2 block">
                {t('auth.fields.confirmPassword')}
              </label>
              <input
                data-testid="signup-confirm-password-input"
                type="password"
                required
                className="w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-none px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7]/20 dark:focus:ring-blue-400/20 focus:border-[#002FA7] dark:focus:border-blue-400 focus:outline-none font-dmsans text-zinc-900 dark:text-zinc-100"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              />
            </div>

            <button
              data-testid="signup-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#002FA7] dark:bg-blue-600 text-white rounded-none px-6 py-2.5 hover:bg-[#001B61] dark:hover:bg-blue-700 transition-colors font-medium border border-transparent disabled:opacity-50"
            >
              {loading ? t('auth.signup.submitting') : t('auth.signup.submit')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="font-dmsans text-sm text-zinc-600 dark:text-zinc-400">
              {t('auth.signup.hasAccount')}{' '}
              <Link to="/login" className="text-[#002FA7] dark:text-blue-400 hover:text-[#001B61] dark:hover:text-blue-300 font-medium">
                {t('auth.fields.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
